import * as THREE from "three"


import ScreenCamera from "./ScreenCamera.js"
import Time from "../../../Utils/Time"
import Sizes from "../../../Utils/Sizes"
import ScreenRenderer from "./ScreenRenderer.js"
import Projects from "./Projects.js"
import crtVertexShader from "../../../shaders/Screen/crtVertex.glsl"
import crtFragmentShader from "../../../shaders/Screen/crtFragment.glsl"
import { EffectComposer, RenderPass, ShaderPass, UnrealBloomPass } from "three/examples/jsm/Addons.js"
import Welcome from "./Welcome.js"
import Experience from "../../../Experience.js"
import Stars from "./Stars.js"
import Interaction from "./Interaction.js"
import Description from "./Description.js"



let instance = null
export default class Home {

    constructor(canvas) {

        if (instance) {
            return instance
        }

        instance = this

        // Options
        this.canvas = canvas

        // Setup
        this.sizes = new Sizes()
        this.time = new Time()
        this.scene = new THREE.Scene()
        this.camera = new ScreenCamera()
        this.renderer = new ScreenRenderer()
        this.composer = new EffectComposer(this.renderer.instance)
        this.composer.setSize(this.sizes.width, this.sizes.height)
        this.composer.setPixelRatio(this.sizes.pixelRatio)
        this.experience = new Experience()
        this.stars = new Stars()
        this.interaction = new Interaction()
        this.resources = this.experience.resources


        // PostProcess (Should be called before pages to ensure composer is ready)
        this.shader()

        // Pages
        this.currentPage = null
        this.previousPage = null
        this.previousPageArgs = []
        this.handleZoomChange = ({ detail }) => {
            if (!detail?.isZoomedIn && !(this.currentPage instanceof Welcome)) {
                this.setPage(Welcome)
            }
        }
        window.addEventListener("console-zoom-change", this.handleZoomChange)

        // Sizes resize event
        this.sizes.on("resize", () => {
            this.resize()
        })

        // Time tick event
        this.time.on("tick", () => {
            this.update()
        })

        // Check if resources are already ready for Home specific textures
        if (this.resources.loaded === this.resources.toLoad) {
            this.setup()
        }
        else {
            this.resources.on("ready", () => {
                this.setup()
            })
        }

    }

    setup() {
        this.setPage(Welcome)
        if (window.parent && typeof window.parent.onIframeReady === "function") {
            window.parent.onIframeReady()
        }
    }

    setPage(PageClass, ...args) {
        if (this.currentPage) {
            if (this.currentPage.constructor !== Description) {
                this.previousPage = this.currentPage.constructor
                this.previousPageArgs = this.currentPageArgs || []
            }
            if (this.currentPage.destroy) {
                this.currentPage.destroy()
            }
        }

        this.currentPageArgs = args
        this.currentPage = new PageClass(...args)
    }

    shader() {

        this.renderPass = new RenderPass(this.scene, this.camera.instance)
        this.composer.addPass(this.renderPass)

        // CRT Shader
        this.crtShader = new THREE.ShaderMaterial({
            uniforms: {
                tDiffuse: { value: null },
                time: { value: 0 },
                uResolution: { value: new THREE.Vector2(this.sizes.width, this.sizes.height) }
            },
            vertexShader: crtVertexShader,
            fragmentShader: crtFragmentShader
        })
        this.bloomPass = new UnrealBloomPass()
        this.bloomPass.radius = 1
        this.bloomPass.strength = 0.39
        this.bloomPass.threshold = 0.1
        this.composer.addPass(this.bloomPass)
        this.crtPass = new ShaderPass(this.crtShader)
        this.composer.addPass(this.crtPass)
        

        // Debug GUI for Bloom Parameters
        const setupBloomDebug = () => {
            let debugGui = null;
            try {
                if (window.parent && window.parent.experience && window.parent.experience.debug && window.parent.experience.debug.active) {
                    debugGui = window.parent.experience.debug.gui;
                }
            } catch (e) {}

            if (!debugGui && this.experience.debug && this.experience.debug.active) {
                debugGui = this.experience.debug.gui;
            }

            if (debugGui) {
                this.bloomFolder = debugGui.addFolder("Bloom");
                this.bloomFolder.add(this.bloomPass, "strength").min(0.0).max(2.0).step(0.01).name("Strength");
                this.bloomFolder.add(this.bloomPass, "radius").min(0.0).max(1.0).step(0.01).name("Radius");
                this.bloomFolder.add(this.bloomPass, "threshold").min(0.0).max(1.0).step(0.01).name("Threshold");
                return true;
            }
            return false;
        };

        if (!setupBloomDebug()) {
            const interval = setInterval(() => {
                if (setupBloomDebug()) {
                    clearInterval(interval);
                }
            }, 100);
            setTimeout(() => clearInterval(interval), 5000);
        }

    }


    resize() {
        this.camera.resize()
        this.renderer.resize()
        this.composer.setSize(this.sizes.width, this.sizes.height)
        this.composer.setPixelRatio(this.sizes.pixelRatio)
    }

    update() {
        this.camera.update()
        this.composer.render()
        this.stars.update()
        if (this.crtShader) {
            this.crtShader.uniforms.time.value = this.time.elapsedTime
            this.crtShader.uniforms.uResolution.value.set(this.sizes.width, this.sizes.height)
        }
    }
}
